import { appApi } from "src/api/api";
import { chatApi } from "src/api/chatApi";
import { DialogType, MessageType, SystemMessageType } from "src/close-part/features/Chat/types";

const appChatApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    getChatAuthToken: builder.mutation<{ token: string }, void>({
      query: () => ({
        method: "POST",
        url: "/chat/request-token",
      })
    }),

    getDialogByUserId: builder.query<unknown, { userId: string }>({
      query: ({ userId }) => ({
        url: "/dialogs/get-dialog-by-recipient-id",
        params: {
          resipient_id: userId,
        }
      })
    }),

    initDialog: builder.mutation<DialogApi, { userId: string, message: string}>({
      query: ({ userId, message }) => ({
        method: "POST",
        url: "/chat/init-dialog",
        body: {
          recipient_id: userId,
          message,
        }
      })
    }),
  }),
});

type GetDialogsResponse = {
  dialogs: DialogApi[],
  last_evaluated_key: unknown,
};

type IAmSpeakerAtTheEventMetadata = {
  type: SystemMessageType.IAmSpeakerAtTheEvent;
  data: {
    event_name: string;
    event_id: string;
    event_date: string;
  }
}

type YouHaveBeenInvitedToBeASpeakerMetadata = {
  type: SystemMessageType.YouHaveBeenInvitedToBeASpeaker;
  data: {
    event_name: string;
    event_id: string;
    event_date: string;
    organizer_id: string;
  }
}

type EventDetailsHaveChangedMetadata = {
  type: SystemMessageType.EventDetailsHaveChanged;
  data: {
    event_name: string;
    event_id: string;
    new_date?: string;
    new_start_time?: string;
    new_end_time?: string;
  }
}

type NewSpeakerAtTheEventMetadata = {
  type: SystemMessageType.NewSpeakerAtTheEvent;
  data: {
    event_name: string;
    event_id: string;
    speaker_id: string;
  }
}

type WelcomeToTheEventMetadata = {
  type: SystemMessageType.WelcomeToTheEvent;
  data: {
    event_name: string;
    event_id: string;
  }
}

type TheEventHasBeenCancelledMetadata = {
  type: SystemMessageType.TheEventHasBeenCancelled;
  data: {
    event_name: string;
  }
}

type IAcceptedToBeASpeakerAtTheEventMetadata = {
  type: SystemMessageType.IAcceptedToBeASpeakerAtTheEvent;
  data: {
    event_name: string;
    event_id: string;
    event_date: string;
    event_price: string;
  }
}

type IAmOrganizingTheEventMetadata = {
  type: SystemMessageType.IAmOrganizingTheEvent;
  data: {
    event_name: string;
    event_id: string;
    event_date: string;
  }
}

type IWillNotBeASpeakerAtTheEventMetadata = {
  type: SystemMessageType.IWillNotBeASpeakerAtTheEvent;
  data: {
    event_name: string;
    event_id: string;
  }
}

type InvitationToGoLiveMetadata = {
  type: SystemMessageType.InvitationToGoLive;
  data: {
    event_id: string;
    session_id: string;
    cancelled_by_speaker: boolean;
    cancelled_by_organizer: boolean;
  };
  sender_id: string;
}

type EventDurationExceededMetadata = {
  type: SystemMessageType.EventDurationExceeded;
}

type WelcomeToEvvveMetadata = {
  type: SystemMessageType.WelcomeToEvvve;
}

type TheEventStartsIn3HoursMetadata = {
  type: SystemMessageType.TheEventStartsIn3Hours;
  data: {
    event_name: string;
    event_id: string;
  };
}

type TheEventStartsIn15MinMetadata = {
  type: SystemMessageType.TheEventStartsIn15Min;
  data: {
    event_name: string;
    event_id: string;
  };
}

type TheEventIsLiveMetadata = {
  type: SystemMessageType.TheEventIsLive;
  data: {
    event_name: string;
    event_id: string;
  };
}

export type SystemMetadata = IAmSpeakerAtTheEventMetadata |
EventDurationExceededMetadata |
YouHaveBeenInvitedToBeASpeakerMetadata |
EventDetailsHaveChangedMetadata |
NewSpeakerAtTheEventMetadata |
TheEventHasBeenCancelledMetadata |
WelcomeToTheEventMetadata |
IAcceptedToBeASpeakerAtTheEventMetadata |
IWillNotBeASpeakerAtTheEventMetadata |
IAmOrganizingTheEventMetadata|
InvitationToGoLiveMetadata |
WelcomeToEvvveMetadata |
TheEventStartsIn3HoursMetadata |
TheEventStartsIn15MinMetadata |
TheEventIsLiveMetadata;

type ApiUserMessage = {
  id: string;
  type: MessageType.User;
  user: {
    id: string;
    data: {
      name: string;
      avatar_link: string;
    }
  } | null,
  text: string | null;
  datetime: string;
};

type ApiSystemMessage = {
  id: string;
  type: MessageType.System;
  datetime: string;
  system_metadata: SystemMetadata;
}

export type ApiMessageType = ApiUserMessage | ApiSystemMessage;

type ApiDialogLastUserMessage = {
  type: MessageType.User;
  username: string;
  text: string;
  datetime: string;
};

type ApiDialogLastSystemMessage = {
  type: MessageType.System;
  system_metadata: SystemMetadata;
  datetime: string;
};

type ApiDialogLastMessage = ApiDialogLastUserMessage | ApiDialogLastSystemMessage;

export type DialogApi = {
  id: string;
  type: DialogType;
  system: boolean;
  title: string;
  avatar_url: string;
  chat_id: string;
  unread_messages_count: number;
  last_message: ApiDialogLastMessage | null;
};

type GetMessagesResponse = {
  messages: ApiMessageType[];
  last_evaluated_key: unknown;
};

type GetMembersResponse = {
  members: Array<{id: string; data: { name: string; avatar_link: string | null }}>
};

type InitMessagesHistoryResponse = {
  oldest: {
    messages: ApiMessageType[],
    last_evaluated_key: unknown,
  } | null,
  newest: {
    messages: ApiMessageType[],
    last_evaluated_key: unknown,
  } | null,
  last_read_message: ApiMessageType | null,
}

const messagesChatApi = chatApi.injectEndpoints({
  endpoints: (builder) => ({
    getIfHasUnreadMessages: builder.query<{ unread_messages: boolean }, void>({
      query: () => ({
        url: "/messages/unread-messages"
      })
    }),

    getDialogs: builder.mutation<GetDialogsResponse, { lastEvaluatedKey?: unknown } | void>({
      query: (args) => ({
        method: "POST",
        url: "/dialogs/get-dialogs",
        body: { last_evaluated_key: args?.lastEvaluatedKey }
      })
    }),

    getDialogById: builder.query<DialogApi, { dialogId: string }>({
      query: ({ dialogId }) => ({
        url: "/dialogs/get-dialog-by-chat-id",
        params: { chat_id: dialogId },
      })
    }),

    getDialogByEventId: builder.query<DialogApi, { eventId: string }>({
      query: ({ eventId }) => ({
        url: "/dialogs/get-dialog-by-chat-id",
        params: { chat_id: eventId },
      })
    }),

    getDialogByUserId: builder.query<DialogApi, { userId: string }>({
      query: ({ userId }) => ({
        url: "/dialogs/get-dialog-by-recipient",
        params: {
          recipient_id: userId,
        }
      })
    }),

    initMessagesHistory: builder.mutation<InitMessagesHistoryResponse, { chatId: string }>({
      query: ({ chatId }) => ({
        method: "POST",
        url: "/messages/init-messages-history",
        body: {
          chat_id: chatId,
        }
      }),
    }),

    getMessages: builder.
      mutation<GetMessagesResponse, { chatId: string, lastEvaluatedKey?: unknown, reverse?: boolean }>({
      query: ({ chatId, lastEvaluatedKey, reverse }) => ({
        method: "POST",
        url: "/messages/get-messages",
        body: { chat_id: chatId, last_evaluated_key: lastEvaluatedKey, reverse },
      })
    }),

    getMembers: builder.query<GetMembersResponse, { chatId: string }>({
      query: ({ chatId }) => ({
        url: `/chat/${chatId}/members`,
      })
    }),

    deleteDialog: builder.mutation<void, {chatId: string;}>({
      query: ({ chatId }) => ({
        method: "POST",
        url: "/dialogs/delete-dialog",
        body: { chat_id: chatId },
      })
    }),

    returnEventGroupChat: builder.mutation<void, { eventId: string }>({
      query: ({ eventId }) => ({
        method: "POST",
        url: "/dialogs/return-dialog",
        body: { chat_id: eventId },
      })
    }),
  })
});

export const {
  useGetChatAuthTokenMutation,
  useInitDialogMutation,
} = appChatApi;

export const {
  useGetIfHasUnreadMessagesQuery,
  useGetDialogsMutation,
  useGetDialogByIdQuery,
  useLazyGetDialogByIdQuery,
  useGetDialogByEventIdQuery,
  useGetDialogByUserIdQuery,
  useLazyGetDialogByUserIdQuery,
  useInitMessagesHistoryMutation,
  useGetMessagesMutation,
  useGetMembersQuery,
  useLazyGetMembersQuery,
  useDeleteDialogMutation,
  useReturnEventGroupChatMutation,
} = messagesChatApi;
